import React, { useState, useEffect, useMemo, Suspense, lazy } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  CircularProgress,
  Tooltip,
  useMediaQuery,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  PhotoLibrary,
  Upload,
  Analytics,
  Brightness4,
  Brightness7,
  AccountCircle,
  Security,
  ExitToApp,
} from '@mui/icons-material';

import ImageGallery from './components/ImageGallery';
import Login from './components/Login';
import { getTheme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { apiService } from './services/api';

const ImageUpload = lazy(() => import('./components/ImageUpload'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const AccessTokenManager = lazy(() => import('./components/AccessTokenManager'));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      tabIndex={0}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

function AuthenticatedApp() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved) return saved === 'dark';
    return !window.matchMedia?.('(prefers-color-scheme: light)').matches;
  });
  const [tabValue, setTabValue] = useState(0);
  const [uploadCounter, setUploadCounter] = useState(0);
  const [stats, setStats] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, isAuthenticated, isGuest, logout } = useAuth();
  const isSmall = useMediaQuery('(max-width:599.95px)');

  const theme = useMemo(() => getTheme(darkMode ? 'dark' : 'light'), [darkMode]);

  useEffect(() => {
    localStorage.setItem('themeMode', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiService.getStats();
      setStats(response.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleUploadComplete = () => {
    fetchStats();
    // Increment upload counter to notify gallery to refresh
    setUploadCounter((c) => c + 1);
    if (tabValue !== 0) {
      setTabValue(0);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleMenuClose();
  };

  // Guest users have limited access
  const availableTabs = isGuest ? [
    { icon: <Badge badgeContent={stats?.total_images || 0} color="primary"><PhotoLibrary /></Badge>, label: "Gallery", id: "tab-0" }
  ] : [
    { icon: <Badge badgeContent={stats?.total_images || 0} color="primary"><PhotoLibrary /></Badge>, label: "Gallery", id: "tab-0" },
    { icon: <Upload />, label: "Upload", id: "tab-1" },
    { icon: <Analytics />, label: "Dashboard", id: "tab-2" },
    { icon: <Security />, label: "Access Links", id: "tab-3" }
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute', left: 8, top: -60, zIndex: 2000, p: 1.5, borderRadius: 1,
          bgcolor: 'primary.main', color: '#fff', '&:focus': { top: 8 },
        }}
      >
        Skip to content
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: 'background.default' }}>
        <AppBar position="static" elevation={2} component="header" sx={{ pt: 'env(safe-area-inset-top)' }}>
          <Toolbar sx={{ gap: 0.5, minHeight: { xs: 56, sm: 64 } }}>
            <PhotoLibrary aria-hidden sx={{ mr: { xs: 1, sm: 2 }, color: 'primary.main' }} />
            <Typography variant="h6" component="h1" noWrap sx={{ flexGrow: 1, fontWeight: 'bold', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
              Collection{isGuest && ' (Guest)'}
            </Typography>
            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
              {stats && `${stats.total_images} images`}
            </Typography>
            <Tooltip title={darkMode ? 'Switch to light theme' : 'Switch to dark theme'}>
              <IconButton color="inherit" onClick={toggleDarkMode} aria-label={darkMode ? 'Switch to light theme' : 'Switch to dark theme'}>
                {darkMode ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Tooltip>

            {/* User Menu */}
            <Tooltip title="Account">
              <IconButton
                color="inherit"
                onClick={handleMenuClick}
                aria-label="Account menu"
                aria-haspopup="true"
                aria-expanded={Boolean(anchorEl)}
              >
                <AccountCircle />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {user && (
                <MenuItem disabled>
                  Logged in as: {user.username}
                </MenuItem>
              )}
              {isGuest && (
                <MenuItem disabled>
                  Guest Access
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} />
                {isGuest ? 'Exit' : 'Logout'}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" component="main" id="main-content" tabIndex={-1} sx={{ mt: { xs: 1.5, sm: 3 }, pb: 4, px: { xs: 1, sm: 3 }, outline: 'none' }}>
          {availableTabs.length > 1 && (
          <Box sx={{
            borderColor: 'divider',
            mb: { xs: 1.5, sm: 3 },
            borderRadius: '8px 8px 0 0',
            backgroundColor: 'background.paper',
            px: { xs: 0, sm: 1 },
          }}>
            <Tabs
              value={Math.min(tabValue, availableTabs.length - 1)}
              onChange={handleTabChange}
              aria-label="Collection sections"
              variant={isSmall ? 'scrollable' : 'fullWidth'}
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  minWidth: { xs: 72, sm: 120 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '1.2rem', sm: '1.2rem' },
                  },
                },
              }}
            >
              {availableTabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  label={tab.label}
                  id={tab.id}
                  aria-controls={`tabpanel-${index}`}
                  sx={{
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                />
              ))}
            </Tabs>
          </Box>
          )}

          <Box sx={{
            backgroundColor: 'background.paper',
            borderRadius: '12px',
            p: { xs: 0.5, sm: 2 },
            minHeight: '500px',
          }}>
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress aria-label="Loading" /></Box>}>
            <TabPanel value={Math.min(tabValue, availableTabs.length - 1)} index={0}>
              <ImageGallery onStatsUpdate={fetchStats} uploadCounter={uploadCounter} />
            </TabPanel>
            {!isGuest && (
              <>
                <TabPanel value={tabValue} index={1}>
                  <ImageUpload onUploadComplete={handleUploadComplete} />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                  <Dashboard stats={stats} onRefresh={fetchStats} />
                </TabPanel>
                <TabPanel value={tabValue} index={3}>
                  <AccessTokenManager />
                </TabPanel>
              </>
            )}
            </Suspense>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return <AuthenticatedApp />;
}

export default App;
